import { Background, QuickCheckoutWrapper } from './styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { select3DSecure, selectCartId, selectComplete3DSCartError } from 'store/cart/selectors';
import { Container } from './components/container';
import { DefaultLoader } from 'components/default-loader';
import { useBooleanState } from '@hqo/react-components-library';
import { useCart } from 'hooks/use-cart.hook';
import { usePaymentMethods } from 'hooks/use-payment-methods.hook';
import { useSelector } from 'react-redux';
import { QuickCheckoutContentProps } from './types';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { Spinner } from '@hqo/react-components-library/dist/atoms/spinner';
import { defaultTheme } from '@hqo/react-components-library/dist/molecules/theme';
import { Container as SpinnerContainer } from 'hocs/shared-styles';
import { ThreeDSModal } from 'components/modals/3dsModal';

const renderLoading = (isCartLoading: boolean, isPaymentMethodsLoading: boolean) =>
  (isCartLoading || isPaymentMethodsLoading) && <DefaultLoader isModal />;

export const QuickCheckoutContent = ({ closeQuickCheckoutHandler }: QuickCheckoutContentProps) => {
  const { cartId: currentCartId } = useSearchParams();
  const cartId = useSelector(selectCartId) || (currentCartId as string);
  const { is3DSecure } = useSelector(select3DSecure);
  const complete3DSCartError = useSelector(selectComplete3DSCartError);
  const { isCartLoading, isCartFulfilled, cart } = useCart(cartId);
  const { isPaymentMethodsLoading, paymentMethods, isPaymentMethodsFullfiled } = usePaymentMethods(cartId);

  const { value: isProcessPaymentFailed, setTrue: setProcessPaymentFailed } = useBooleanState();
  const [show3DS, setShow3DS] = useState<boolean>(false);

  useEffect(() => {
    if (complete3DSCartError) {
      setProcessPaymentFailed();
    }
  }, [complete3DSCartError, setProcessPaymentFailed]);

  const setPaymentError = useCallback(
    (value: boolean) => {
      if (value) {
        setProcessPaymentFailed();
      }
    },
    [setProcessPaymentFailed],
  );

  const paymentMethod = paymentMethods?.length
    ? paymentMethods?.find(method => method.default) || paymentMethods[0]
    : null;

  const [currentOffsetHeight, setOffsetHeight] = useState<number>(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      setOffsetHeight(contentRef.current.offsetHeight);
    }
  }, [contentRef]);

  return (
    <>
      {!show3DS ? (
        <>
          <QuickCheckoutWrapper data-testid="quick-checkout-swipe-modal" ref={contentRef}>
            <>
              {renderLoading(isCartLoading, isPaymentMethodsLoading)}
              {isCartFulfilled && isPaymentMethodsFullfiled && (
                <Container
                  closeQuickCheckoutHandler={closeQuickCheckoutHandler}
                  cart={cart}
                  isProcessPaymentFailed={isProcessPaymentFailed}
                  setProcessPaymentFailed={setProcessPaymentFailed}
                  paymentMethods={paymentMethods}
                  currentPaymentMethodId={paymentMethod?.id?.toString()}
                />
              )}
            </>
          </QuickCheckoutWrapper>
          <Background />
        </>
      ) : (
        <SpinnerContainer data-testid="loading-container" isLoading isModal>
          <Spinner size="10em" color={defaultTheme.colors.$greyLight} />
        </SpinnerContainer>
      )}
      {is3DSecure && (
        <ThreeDSModal
          setPaymentError={setPaymentError}
          setIs3DSVisible={setShow3DS}
          transactionToken={cart?.transaction?.token}
          spreedlyToken={cart?.ui_enablements.spreedly_env_token}
          currentOffsetHeight={currentOffsetHeight}
          isQuickCheckout
        />
      )}
    </>
  );
};
